import React, { useMemo } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { gbp } from "@/lib/stages";
import { ukDayTime, ukDayYear } from "@/lib/time";
import { MONTHS } from "@/lib/caseflow";
import {
  Bar, Btn, CellMain, CellSub, Conf, DeskSupport, Divider, Empty, Kv, LinkRow, Money, Note, Opt, PageBody, PageHead, Panel, RailCard, RangeBar, Sec, Tb, Td, Th,
} from "@/components/portal/crm/kit";

const CONF = { funded: "h", offer: "h", live: "m", panel: "l", passed: "l" };
const CONF_LABEL = {
  funded: "Confirmed", offer: "Offer on the table", live: "In review", panel: "Too early", passed: "Closed",
};

export default function Earnings() {
  const { projection, summary } = useOutletContext();
  const nav = useNavigate();
  const t = projection?.totals;
  const cases = useMemo(() => projection?.cases || [], [projection]);

  /* One source of truth for the rate: the server works out the wording from the partner's own
     model, so a figure can never be rendered twice and disagree with itself. */
  const m = projection?.model;
  const weekly = (summary?.payout_frequency || "monthly") === "weekly";
  /* With one desk pricing every live case there is no spread, and "£366,063 – £366,063" reads
     as a bug rather than as a range. */
  const sameRange = (t?.min_gbp || 0) === (t?.max_gbp || 0);

  /* The widest live case is the one worth explaining — it is where a range comes from. */
  const widest = useMemo(() => {
    const live = cases.filter((c) => c.counted && !c.single);
    return live.sort((a, b) => (b.max_gbp - b.min_gbp) - (a.max_gbp - a.min_gbp))[0] || null;
  }, [cases]);

  const owed = summary?.owed_gbp || 0;
  const paid = summary?.paid_gbp || 0;
  const year = owed + paid;
  const funded = summary?.completed || 0;
  const avg = funded ? year / funded : 0;

  const rail = (
    <>
      <Panel title="Your year" testId="referrer-earnings-year">
        <div className="money" style={{ fontSize: 30 }}>{gbp(year)}</div>
        <div className="money-sub">Paid and owed, {new Date().getUTCFullYear()} to date</div>
        <Bar pct={year ? (paid / year) * 100 : 0} style={{ marginTop: 16 }} />
        <div className="ladder-lab"><span>{gbp(paid)} paid</span><span>{gbp(owed)} owed</span></div>
        <Divider style={{ margin: "18px 0" }} />
        <Kv rows={[
          ["Funded deals", String(funded)],
          ["Average fee", gbp(avg)],
        ]} />
      </Panel>

      <DeskSupport body="Query a figure and we will walk you through the arithmetic on the case."
                   testId="referrer-earnings-support" />

      <RailCard title="Statements" testId="referrer-earnings-statements">
        {[0, 1].map((back) => {
          const d = new Date();
          d.setUTCMonth(d.getUTCMonth() - back - 1);
          return (
            <LinkRow key={back} label={`${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`}
                     onClick={() => nav("/introducer/statements")} />
          );
        })}
        <LinkRow label="Payout bank details" onClick={() => nav("/introducer/account")} />
      </RailCard>

      <Note testId="referrer-projection-note">{projection?.note}</Note>
    </>
  );

  return (
    <>
      <PageHead
        eyebrow="Live, recalculated on every desk reply"
        title="Projected earnings"
        lede="What your live cases are worth to you if they complete, worked out from your agreement and the terms we hold with each desk the case is sitting with. A projection is never a promise, and we say so on every figure."
        testId="referrer-performance-head"
        actions={
          <>
            <Btn href="#how" testId="referrer-earnings-how">How this is calculated</Btn>
            <Btn kind="primary" as={Link} to="/introducer/statements">Download statement</Btn>
          </>
        }
      />

      <PageBody rail={rail}>
        <div className="stats" data-testid="referrer-earnings-stats">
          <div className="stat">
            <div className="k">Paid to you</div>
            <div className="v"><span className="cur">£</span>{gbp(paid).slice(1)}</div>
            <div className="h">{summary?.last_payout_at ? <>Last payout <b>{ukDayYear(summary.last_payout_at)}</b></> : "Nothing settled yet"}</div>
          </div>
          <div className="stat">
            <div className="k">Owed, awaiting payout</div>
            <div className="v" style={{ color: "var(--green)" }}><span className="cur">£</span>{gbp(owed).slice(1)}</div>
            <div className="h">Confirmed. Pays <b>{ukDayYear(summary?.next_payout_at, "on the next run")}</b></div>
          </div>
          <div className="stat">
            <div className="k">Projected, most likely</div>
            <div className="v"><span className="cur">£</span>{gbp(t?.likely_gbp).slice(1)}</div>
            <div className="h">Across {t?.live_cases ?? 0} live case{t?.live_cases === 1 ? "" : "s"}</div>
          </div>
          <div className="stat">
            <div className="k">{sameRange ? "Average per live case" : "Projected range"}</div>
            {sameRange ? (
              <>
                <div className="v"><span className="cur">£</span>{gbp(t?.live_cases ? (t.likely_gbp || 0) / t.live_cases : 0).slice(1)}</div>
                <div className="h">One desk is pricing each case, so there is no range yet</div>
              </>
            ) : (
              <>
                <div className="v" style={{ fontSize: 21, whiteSpace: "nowrap" }}>
                  <span className="cur">£</span>{gbp(t?.min_gbp).slice(1)}
                  <span style={{ color: "var(--mut-2)", fontWeight: 700 }}> – </span>
                  <span className="cur">£</span>{gbp(t?.max_gbp).slice(1)}
                </div>
                <div className="h">Widest and narrowest outcome</div>
              </>
            )}
          </div>
        </div>

        {widest && (
          <Sec
            title="Why a live case shows a range"
            sub={`${widest.business_name || widest.applicant_name || widest.label || "A case from your campaign"} is with ${widest.desks_live} desk${widest.desks_live === 1 ? "" : "s"}. Each desk pays us a different fee, so your share depends on which one ends up funding it.`}
          >
            <div className="card">
              <div className="card-b">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
                  <div>
                    <div className="cell-main" style={{ fontSize: 17 }}>{widest.business_name || widest.applicant_name || widest.label || "A case from your campaign"}</div>
                    <CellSub>{widest.finance_type} · {gbp(widest.amount_gbp)} · with {widest.desks_live} desk{widest.desks_live === 1 ? "" : "s"}</CellSub>
                  </div>
                  <Conf level={CONF[widest.state] || "l"}>
                    {widest.desks_passed > 0 ? `Narrowing — ${widest.desks_passed} desk${widest.desks_passed === 1 ? " has" : "s have"} passed` : CONF_LABEL[widest.state]}
                  </Conf>
                </div>

                <RangeBar min={widest.min_gbp} likely={widest.likely_gbp} max={widest.max_gbp} testId="referrer-projection-bar" />

                <p style={{ color: "var(--mut)", fontSize: 13, margin: "22px 0 0", maxWidth: "72ch" }}>{widest.basis}</p>

                <Divider />

                <div className="rail-t" style={{ marginBottom: 14 }}>How it narrows</div>
                <div className="narrow-grid">
                  {[
                    ["Sent to desks", `${gbp(widest.min_gbp)} – ${gbp(widest.max_gbp)}`, "Widest it ever gets", widest.state === "panel"],
                    ["A desk reads it", `${gbp(widest.min_gbp)} – ${gbp(widest.max_gbp)}`, "Still every desk in play", widest.state === "live"],
                    ["Offer out", gbp(widest.likely_gbp), "Terms on the table", widest.state === "offer"],
                    ["Funded", gbp(widest.likely_gbp), "Confirmed, moves to owed", widest.state === "funded"],
                    ["Paid", gbp(widest.likely_gbp), "On your statement", false],
                  ].map(([title, figure, sub, pick]) => (
                    <Opt key={title} pick={pick} style={{ padding: 15 }}>
                      <div className="t" style={{ fontSize: 13 }}>{title}</div>
                      <div className="money" style={{ fontSize: 14, marginTop: 9 }}>{figure}</div>
                      <div className="money-sub">{pick ? `${sub}. You are here` : sub}</div>
                    </Opt>
                  ))}
                </div>
              </div>
            </div>
          </Sec>
        )}

        <Sec title="Every live case" sub="A case only carries a figure once a desk has accepted it for review. Before that it is not counted at all.">
          <div className="card" data-testid="referrer-projection-cases">
            {cases.length === 0 ? (
              <Empty testId="referrer-projection-empty">Nothing introduced yet, so there is nothing to project.</Empty>
            ) : (
              <Tb head={<>
                <Th style={{ width: "26%" }}>Case</Th>
                <Th style={{ width: "16%" }}>Facility</Th>
                <Th style={{ width: "19%" }}>Desks holding it</Th>
                <Th style={{ width: "22%" }}>Projected to you</Th>
                <Th className="r" style={{ width: "17%" }}>Confidence</Th>
              </>}>
                {cases.map((c, i) => (
                  <tr key={c.lead_id || c.reference || i} className={c.state === "passed" ? "flag" : ""} style={{ cursor: c.lead_id ? "pointer" : "default" }}
                      onClick={() => c.lead_id && nav(`/introducer/cases/${c.lead_id}`)} data-testid={`referrer-projection-row-${c.lead_id || i}`}>
                    <td className="lead">
                      <CellMain>{c.business_name || c.applicant_name || c.label || "Client via your campaign"}</CellMain>
                      <CellSub>{c.finance_type}{c.reference ? ` · ${c.reference}` : ""}</CellSub>
                    </td>
                    <Td><Money v={c.amount_gbp} /></Td>
                    <Td>
                      <Money raw={c.state === "funded" ? "Funded" : c.state === "passed" ? "All passed" : `${c.desks_live} desk${c.desks_live === 1 ? "" : "s"}`} tone="muted" size="sm" />
                      <div className="money-sub">
                        {c.state === "funded"
                          ? "The desk that funded it"
                          : c.state === "panel"
                            ? `${c.desks_considered} on our panel fit it`
                            : [c.desks_live ? `${c.desks_live} live` : null, c.desks_passed ? `${c.desks_passed} passed` : null].filter(Boolean).join(", ") || "—"}
                      </div>
                    </Td>
                    <Td>
                      {/* A funded or closed case is not a projection — saying "not counted yet" beside
                          "Funded" reads as a contradiction, which is exactly what it was. */}
                      {c.state === "funded"
                        ? <Money v={c.likely_gbp} tone="green" sub="Confirmed — in your owed figure" />
                        : c.state === "passed"
                          ? <Money v={c.likely_gbp} tone={c.likely_gbp ? "ink" : "muted"}
                                   sub={c.likely_gbp ? "Already earned" : "No fee — every desk passed"} />
                          : c.counted
                            ? <Money v={c.likely_gbp} sub={c.single ? "If it completes" : `${gbp(c.min_gbp)} – ${gbp(c.max_gbp)}`} />
                            : <Money raw="Not counted yet" tone="muted" sub="Counts when a desk accepts it" />}
                    </Td>
                    <Td className="r"><Conf level={CONF[c.state] || "l"}>{CONF_LABEL[c.state] || "Too early"}</Conf></Td>
                  </tr>
                ))}
              </Tb>
            )}
            <div className="card-f">
              <span>Figures exclude VAT. A case that completes at a different facility size recalculates the same day.</span>
              <span style={{ fontWeight: 700, color: "var(--ink-3)" }}>{ukDayTime(new Date().toISOString())} UK</span>
            </div>
          </div>
        </Sec>

        <Sec title="How your fee is worked out"
             sub="The same arithmetic the ledger uses when the money actually lands."
             style={{ scrollMarginTop: 90 }}>
          <span id="how" />
          <div className="card">
            <div className="card-b">
              <ol className="method" data-testid="referrer-fee-method">
                {[
                  ["The deal funds", "A desk completes and invoices its fee on the facility."],
                  ["Connexis takes its share", "Our share of that fee is fixed in the desk's own agreement — it varies from desk to desk, which is why your figure is a range until one of them funds it."],
                  [`Your ${m?.rate || "rate"} is applied`, m ? `${m.label}, worked out on that specific deal.` : "Your agreed rate is applied to it."],
                  ["It lands on a statement", `Added to what you are owed, then paid on the ${weekly ? "weekly" : "monthly"} run with a statement on letterhead.`],
                ].map(([title, body], i) => (
                  <li key={title}>
                    <span className="n2">{i + 1}</span>
                    <div>
                      <div className="t3">{title}</div>
                      <div className="b3">{body}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </Sec>

        <Sec title="The rules behind the number" sub="Written down so there is never an argument about a figure on a screen.">
          <div className="card">
            <div className="card-b grid2" data-testid="referrer-projection-rules">
              <div>
                <div className="rail-t">Your agreement</div>
                <Kv left style={{ maxWidth: 400, gap: "11px 26px" }} rows={[
                  ["Your model", <span style={{ fontSize: 13 }} key="m">{m?.short || "—"}</span>],
                  ["Counts from", <span style={{ fontSize: 13 }} key="c">A desk accepts it for review</span>],
                  ["Becomes owed", <span style={{ fontSize: 13 }} key="o">The lender confirms drawdown</span>],
                  ["Paid", <span style={{ fontSize: 13 }} key="p">{weekly ? "Fridays" : "1st of the following month"}</span>],
                ]} />
              </div>
              <div>
                <div className="rail-t">What you will never see</div>
                {/* Aligned to the same left edge as the heading and the key/values opposite — an
                    indented, marker-less list in the corner of a wide card just looks adrift. */}
                <ul style={{ listStyle: "none", margin: "14px 0 0", padding: 0, display: "flex",
                             flexDirection: "column", gap: 10, maxWidth: 400 }}>
                  {["Which desk pays Connexis what",
                    "The name of any desk on your case",
                    "Another partner\u2019s figures"].map((x) => (
                    <li key={x} style={{ display: "flex", gap: 11, alignItems: "baseline",
                                         color: "var(--ink-3)", fontSize: 13, lineHeight: 1.5 }}>
                      <span style={{ color: "var(--mut-2)", flex: "none", fontWeight: 800 }}>&ndash;</span>
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
                <p style={{ color: "var(--mut)", fontSize: 12.5, margin: "16px 0 0", maxWidth: "46ch",
                            lineHeight: 1.65 }}>
                  You see your range and your figure. The panel economics behind it stay with the desk.
                </p>
              </div>
            </div>
          </div>
        </Sec>
      </PageBody>
    </>
  );
}
